import React, { useState, useEffect } from 'react';
import { Card, Typography, Table, message, Spin, Alert, Select, Modal } from 'antd';
import { AppstoreOutlined, BarChartOutlined, LineChartOutlined, StockOutlined, RiseOutlined, DollarOutlined, LoginOutlined } from '@ant-design/icons';
import { Container } from 'react-bootstrap';
import { Nav, Navbar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../user/auth/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { API_CONFIG, apiClient } from '../api/config';
import { logger } from '../utils/logger';
import activityTracker from '../user/ActivityTracker';
import TechnicalChartsPage from './TechnicalChartsPage';
import LoginDialog from '../user/auth/LoginDialog';
import RegisterDialog from '../user/auth/RegisterDialog';
import usePageTracking from '../utils/usePageTracking';
import { 
  basicInfoColumns,
  growthValuationColumns,
  financialRatiosColumns,
  pricePerformanceColumns,
  trendColumns,
  pageNames
} from '../components/TableColumns';
import TableControlBar, { useColumnVisibility } from '../components/ColumnVisibilityControl';

const { Title } = Typography;
const { Option } = Select;

function TopCompanies() {
  // GA4 页面追踪
  usePageTracking('權值列表');
  
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { updateCompanyInfo } = useCompany();
  const [loading, setLoading] = useState(false);
  const [companiesData, setCompaniesData] = useState([]);
  const [tableViewType, setTableViewType] = useState('basic');
  const [selectedSector, setSelectedSector] = useState('');
  const [currentApiType, setCurrentApiType] = useState('marketCap'); // 'marketCap' 或 'sector'
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const tableContainerRef = React.useRef(null);

  // 计算当前6点周期的标识（用于缓存判断）
  const getCurrentCyclePeriod = () => {
    const now = new Date();
    const currentHour = now.getHours();
    
    // 如果当前时间在6点之前，属于前一天6点开始的周期
    if (currentHour < 6) {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
    }
    
    // 如果当前时间在6点之后，属于今天6点开始的周期
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  // 检查缓存是否有效
  const isCacheValid = (cacheKey) => {
    try {
      const cachedData = localStorage.getItem(cacheKey);
      if (!cachedData) {
        return false;
      }

      const { period, data } = JSON.parse(cachedData);
      const currentPeriod = getCurrentCyclePeriod();
      
      // 检查是否在同一个6点周期内
      return period === currentPeriod && data && data.length > 0;
    } catch (error) {
      logger.error('检查缓存有效性时出错:', error);
      return false;
    }
  };

  // 从缓存获取数据
  const getFromCache = (cacheKey) => {
    try {
      const cachedData = localStorage.getItem(cacheKey);
      if (!cachedData) {
        return null;
      }

      const { data } = JSON.parse(cachedData);
      return data;
    } catch (error) {
      logger.error('从缓存获取数据时出错:', error);
      return null;
    }
  };

  // 清理旧的缓存数据
  const cleanOldCache = () => {
    try {
      const currentPeriod = getCurrentCyclePeriod();
      const keysToRemove = [];
      
      // 遍历 localStorage 找出所有过期的 topcompanies 缓存
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('topcompanies_')) {
          try {
            const cachedData = localStorage.getItem(key);
            if (cachedData) {
              const { period } = JSON.parse(cachedData);
              if (period !== currentPeriod) {
                keysToRemove.push(key);
              }
            }
          } catch (e) {
            // 如果解析失败，也标记为删除
            keysToRemove.push(key);
          }
        }
      }
      
      // 删除过期缓存
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        logger.log(`已清理过期缓存: ${key}`);
      });
      
      return keysToRemove.length;
    } catch (error) {
      logger.error('清理旧缓存时出错:', error);
      return 0;
    }
  };

  // 压缩数据 - 只保留必要的字段
  const compressData = (data) => {
    if (!Array.isArray(data)) return data;
    
    return data.map(item => {
      // 只保留必要的字段，去除冗余数据
      return {
        company_symbol: item.company_symbol,
        company_name: item.company_name,
        sector: item.sector,
        industry: item.industry,
        marketCap: item.marketCap,
        price: item.price,
        changes: item.changes,
        changesPercentage: item.changesPercentage,
        image: item.image,
        
        // 财务指标
        pe: item.pe,
        eps: item.eps,
        revenue: item.revenue,
        netIncome: item.netIncome,
        operatingCashFlow: item.operatingCashFlow,
        
        // 增长率
        revenueGrowth: item.revenueGrowth,
        epsGrowth: item.epsGrowth,
        
        // 比率
        debtToEquity: item.debtToEquity,
        currentRatio: item.currentRatio,
        roe: item.roe,
        roa: item.roa,
        
        // 价格表现
        yearHigh: item.yearHigh,
        yearLow: item.yearLow,
        priceChange1D: item.priceChange1D,
        priceChange1W: item.priceChange1W,
        priceChange1M: item.priceChange1M,
        priceChange3M: item.priceChange3M,
        priceChange6M: item.priceChange6M,
        priceChange1Y: item.priceChange1Y,
        
        // 趋势指标
        sma50: item.sma50,
        sma200: item.sma200,
        rsi: item.rsi,
        volume: item.volume,
        avgVolume: item.avgVolume
      };
    });
  };

  // 保存数据到缓存（优化版）
  const saveToCache = (cacheKey, data) => {
    try {
      const currentPeriod = getCurrentCyclePeriod();
      
      // 1. 先清理旧的缓存
      const cleanedCount = cleanOldCache();
      if (cleanedCount > 0) {
        logger.log(`已清理 ${cleanedCount} 个过期缓存`);
      }
      
      // 2. 压缩数据
      const compressedData = compressData(data);
      
      // 3. 准备缓存数据
      const cacheData = {
        period: currentPeriod,
        data: compressedData,
        timestamp: new Date().toISOString()
      };
      
      const cacheString = JSON.stringify(cacheData);
      const cacheSizeKB = (new Blob([cacheString]).size / 1024).toFixed(2);
      
      // 4. 尝试保存
      localStorage.setItem(cacheKey, cacheString);
      logger.log(`数据已保存到缓存，周期: ${currentPeriod}, 缓存键: ${cacheKey}, 大小: ${cacheSizeKB}KB`);
      
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        logger.error('localStorage 配额已满，尝试清理所有 topcompanies 缓存...');
        
        // 尝试清理所有 topcompanies 相关缓存
        try {
          const allKeys = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('topcompanies_')) {
              allKeys.push(key);
            }
          }
          
          allKeys.forEach(key => localStorage.removeItem(key));
          logger.log(`已清理 ${allKeys.length} 个 topcompanies 缓存`);
          
          // 再次尝试保存
          const currentPeriod = getCurrentCyclePeriod();
          const compressedData = compressData(data);
          const cacheData = {
            period: currentPeriod,
            data: compressedData,
            timestamp: new Date().toISOString()
          };
          
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          logger.log('清理后重新保存成功');
          
        } catch (retryError) {
          logger.error('清理后仍无法保存，缓存功能将被禁用:', retryError);
          // 降级：不使用缓存，但不影响功能
        }
      } else {
        logger.error('保存数据到缓存时出错:', error);
      }
    }
  };

  // 產業選項
  const sectorOptions = [
    { value: '', label: '全部產業' },
    { value: 'Basic Materials', label: 'Basic Materials 原材料／基礎材料' },
    { value: 'Communication Services', label: 'Communication Services 通訊服務' },
    { value: 'Consumer Cyclical', label: 'Consumer Cyclical 消費循環性產業／景氣循環消費' },
    { value: 'Consumer Defensive', label: 'Consumer Defensive 消費防禦性產業／必需消費品' },
    { value: 'Energy', label: 'Energy 能源' },
    { value: 'Financial Services', label: 'Financial Services 金融服務' },
    { value: 'Healthcare', label: 'Healthcare 醫療保健／健康照護' },
    { value: 'Industrials', label: 'Industrials 工業' },
    { value: 'Real Estate', label: 'Real Estate 房地產' },
    { value: 'Technology', label: 'Technology 科技' },
    { value: 'Utilities', label: 'Utilities 公用事業' }
  ];

  useEffect(() => {
    activityTracker.trackPageView('top_companies');
    
    // 页面加载时清理旧缓存
    try {
      const currentPeriod = getCurrentCyclePeriod();
      const keysToRemove = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('topcompanies_')) {
          try {
            const cachedData = localStorage.getItem(key);
            if (cachedData) {
              const { period } = JSON.parse(cachedData);
              if (period !== currentPeriod) {
                keysToRemove.push(key);
              }
            }
          } catch (e) {
            keysToRemove.push(key);
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        logger.log(`页面加载时已清理 ${keysToRemove.length} 个过期缓存`);
      }
    } catch (error) {
      logger.error('页面加载时清理缓存失败:', error);
    }
  }, []);

  // 依分頁自動載入資料（访客也可以查看）
  useEffect(() => {
    if (selectedSector) {
      fetchTopSectorCompanies(selectedSector);
    } else {
      fetchTopCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSector]);

  // 處理點擊公司代碼的事件
  const handleSymbolClick = async (symbol) => {
    activityTracker.trackBtnExecution('top_companies', 'symbol_click', `股票代碼點擊_${symbol}`);
    logger.log('TopCompanies: handleSymbolClick 被調用，symbol =', symbol);
    
    if (!isAuthenticated) {
      // 获取当前搜索次数和时间戳
      const lastResetTime = localStorage.getItem('companySearchCountTime');
      const currentTime = new Date().getTime();
      
      // 如果没有时间戳或超过4天，重置计数
      if (!lastResetTime || (currentTime - parseInt(lastResetTime)) > (4 * 24 * 60 * 60 * 1000)) {
        localStorage.setItem('companySearchCount', '0');
        localStorage.setItem('companySearchCountTime', currentTime.toString());
        logger.log('重置搜索計數，因為超過4天');
      }

      // 获取当前计数
      const currentCount = parseInt(localStorage.getItem('companySearchCount')) || 0;

      // 如果超过2次，显示登录提示
      if (currentCount >= 2) {
        message.warning('您已達到未登錄用戶的搜索限制(2次)，請登錄以繼續使用完整功能。');
        setShowLoginDialog(true);
        return;
      }

      // 增加计数
      const newCount = currentCount + 1;
      localStorage.setItem('companySearchCount', newCount.toString());
      localStorage.setItem('companySearchCountTime', currentTime.toString());
    }

    try {
      logger.log('TopCompanies: 開始獲取公司信息，symbol =', symbol);
      
      // 調用公司搜索 API
      const response = await apiClient.get(API_CONFIG.endpoints.companySearch, {
        params: { term: symbol }
      });
      logger.log('TopCompanies: API 響應數據 =', response.data);
      
      if (response.data && response.data.length > 0) {
        const companyInfo = response.data[0];
        logger.log('TopCompanies: 獲取到公司信息 =', companyInfo);
        
        // 將公司信息存儲到 localStorage，供新分頁使用
        localStorage.setItem('tempCompanyInfo', JSON.stringify(companyInfo));
        
        // 在新分頁中開啟資訊主頁，並帶上 symbol 參數
        const newTab = window.open(`/maininfo?symbol=${encodeURIComponent(symbol)}`, '_blank');
        
        // 如果新分頁被阻擋，顯示提示
        if (!newTab) {
          message.warning('請允許彈出視窗以在新分頁中開啟');
        }
      } else {
        logger.error('TopCompanies: 未找到公司信息');
        message.error('未找到公司信息');
      }
    } catch (error) {
      logger.error('TopCompanies: 獲取公司信息失敗 =', error);
      message.error('獲取公司信息失敗，請稍後再試');
    }
  };

  // 處理右鍵選單事件
  const handleSymbolContextMenu = async (e, symbol) => {
    e.preventDefault();
    
    // 先清理可能存在的舊選單
    const existingMenu = document.querySelector('.symbol-context-menu');
    if (existingMenu) {
      try {
        existingMenu.remove();
      } catch (error) {
        // 忽略錯誤，選單可能已經不存在
      }
    }
    
    // 創建自定義右鍵選單
    const menu = document.createElement('div');
    menu.className = 'symbol-context-menu';
    menu.style.cssText = `
      position: fixed;
      top: ${e.clientY}px;
      left: ${e.clientX}px;
      background: white;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
      z-index: 1000;
      min-width: 120px;
      font-size: 14px;
    `;

    const menuItems = [
      { text: '新分頁開啟', action: () => openSymbolInNewTab(symbol) },
      { text: '新視窗開啟', action: () => openSymbolInNewWindow(symbol) }
    ];

    menuItems.forEach(item => {
      const menuItem = document.createElement('div');
      menuItem.textContent = item.text;
      menuItem.style.cssText = `
        padding: 8px 12px;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;
        transition: background-color 0.2s;
      `;
      menuItem.onmouseover = () => {
        menuItem.style.backgroundColor = '#f5f5f5';
      };
      menuItem.onmouseout = () => {
        menuItem.style.backgroundColor = 'white';
      };
      menuItem.onclick = () => {
        item.action();
        closeMenu();
      };
      menu.appendChild(menuItem);
    });

    // 移除最後一個項目的底部邊框
    const lastItem = menu.lastElementChild;
    if (lastItem) {
      lastItem.style.borderBottom = 'none';
    }

    document.body.appendChild(menu);

    // 點擊其他地方關閉選單
    let isMenuClosed = false;
    const closeMenu = () => {
      if (isMenuClosed) return; // 防止重複執行
      isMenuClosed = true;
      
      const menuToRemove = document.querySelector('.symbol-context-menu');
      if (menuToRemove) {
        try {
          menuToRemove.remove();
        } catch (error) {
          // 忽略錯誤，選單可能已經不存在
        }
      }
      
      // 移除事件監聽器
      document.removeEventListener('click', closeMenu);
      document.removeEventListener('contextmenu', closeMenu);
    };

    // 延遲添加事件監聽器，避免立即觸發
    setTimeout(() => {
      document.addEventListener('click', closeMenu);
      document.addEventListener('contextmenu', closeMenu);
    }, 100);
  };

  // 在新分頁中開啟 symbol
  const openSymbolInNewTab = async (symbol) => {
    // 檢查訪客限制
    if (!isAuthenticated) {
      const lastResetTime = localStorage.getItem('companySearchCountTime');
      const currentTime = new Date().getTime();
      
      // 如果没有时间戳或超过4天，重置计数
      if (!lastResetTime || (currentTime - parseInt(lastResetTime)) > (4 * 24 * 60 * 60 * 1000)) {
        localStorage.setItem('companySearchCount', '0');
        localStorage.setItem('companySearchCountTime', currentTime.toString());
      }

      // 获取当前计数
      const currentCount = parseInt(localStorage.getItem('companySearchCount')) || 0;

      // 如果超过2次，显示登录提示
      if (currentCount >= 2) {
        message.warning('您已達到未登錄用戶的搜索限制(2次)，請登錄以繼續使用完整功能。');
        setShowLoginDialog(true);
        return;
      }

      // 增加计数
      const newCount = currentCount + 1;
      localStorage.setItem('companySearchCount', newCount.toString());
      localStorage.setItem('companySearchCountTime', currentTime.toString());
    }

    try {
      // 調用公司搜索 API
      const response = await apiClient.get(API_CONFIG.endpoints.companySearch, {
        params: { term: symbol }
      });
      
      if (response.data && response.data.length > 0) {
        const companyInfo = response.data[0];
        
        // 將公司信息存儲到 localStorage，供新分頁使用
        localStorage.setItem('tempCompanyInfo', JSON.stringify(companyInfo));
        
        // 在新分頁中開啟資訊主頁，並帶上 symbol 參數
        const newTab = window.open(`/maininfo?symbol=${encodeURIComponent(symbol)}`, '_blank');
        
        // 如果新分頁被阻擋，顯示提示
        if (!newTab) {
          message.warning('請允許彈出視窗以在新分頁中開啟');
        }
      } else {
        message.error('未找到公司信息');
      }
    } catch (error) {
      logger.error('在新分頁開啟 symbol 失敗:', error);
      message.error('開啟失敗，請稍後再試');
    }
  };

  // 在新視窗中開啟 symbol
  const openSymbolInNewWindow = async (symbol) => {
    // 檢查訪客限制
    if (!isAuthenticated) {
      const lastResetTime = localStorage.getItem('companySearchCountTime');
      const currentTime = new Date().getTime();
      
      // 如果没有时间戳或超过4天，重置计数
      if (!lastResetTime || (currentTime - parseInt(lastResetTime)) > (4 * 24 * 60 * 60 * 1000)) {
        localStorage.setItem('companySearchCount', '0');
        localStorage.setItem('companySearchCountTime', currentTime.toString());
      }

      // 获取当前计数
      const currentCount = parseInt(localStorage.getItem('companySearchCount')) || 0;

      // 如果超过2次，显示登录提示
      if (currentCount >= 2) {
        message.warning('您已達到未登錄用戶的搜索限制(2次)，請登錄以繼續使用完整功能。');
        setShowLoginDialog(true);
        return;
      }

      // 增加计数
      const newCount = currentCount + 1;
      localStorage.setItem('companySearchCount', newCount.toString());
      localStorage.setItem('companySearchCountTime', currentTime.toString());
    }

    try {
      // 調用公司搜索 API
      const response = await apiClient.get(API_CONFIG.endpoints.companySearch, {
        params: { term: symbol }
      });
      
      if (response.data && response.data.length > 0) {
        const companyInfo = response.data[0];
        
        // 將公司信息存儲到 localStorage，供新視窗使用
        localStorage.setItem('tempCompanyInfo', JSON.stringify(companyInfo));
        
        // 在新視窗中開啟資訊主頁，並帶上 symbol 參數
        const newWindow = window.open(`/maininfo?symbol=${encodeURIComponent(symbol)}`, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
        
        // 如果新視窗被阻擋，顯示提示
        if (!newWindow) {
          message.warning('請允許彈出視窗以在新視窗中開啟');
        }
      } else {
        message.error('未找到公司信息');
      }
    } catch (error) {
      logger.error('在新視窗開啟 symbol 失敗:', error);
      message.error('開啟失敗，請稍後再試');
    }
  };

  // 獲取指定 sector 市值最大的前30檔股票
  const fetchTopSectorCompanies = async (sector) => {
    try {
      setLoading(true);
      
      // 构建缓存键
      const cacheKey = `topcompanies_sector_${sector}`;
      
      // 检查缓存是否有效
      if (isCacheValid(cacheKey)) {
        const cachedData = getFromCache(cacheKey);
        if (cachedData) {
          setCompaniesData(cachedData);
          setCurrentApiType('sector');
          message.success(`從快取獲取 ${sector} 產業 ${cachedData.length} 家市值最大的公司`);
          logger.log(`從快取載入數據，周期: ${getCurrentCyclePeriod()}`);
          setLoading(false);
          return;
        }
      }

      // 缓存无效，从数据库获取
      activityTracker.trackBtnExecution('top_companies', 'fetch_sector_data', `獲取${sector}產業市值最大前30檔股票`);
      logger.log(`快取無效或不存在，從資料庫獲取數據`);

      const response = await apiClient.get(API_CONFIG.endpoints.TOPCOMPANIES.TOP_SECTOR_MARKET_CAP, {
        params: { sector }
      });
      
      if (response.data && response.data.length > 0) {
        setCompaniesData(response.data);
        setCurrentApiType('sector');
        
        // 保存到缓存
        saveToCache(cacheKey, response.data);
        
        message.success(`從資料庫獲取 ${sector} 產業 ${response.data.length} 家市值最大的公司`);
      } else {
        message.warning(`未找到 ${sector} 產業的公司數據`);
      }
    } catch (error) {
      logger.error('獲取指定 sector 市值最大前30檔股票時出錯:', error);
      message.error('獲取數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  // 獲取市值最大的200檔股票
  const fetchTopCompanies = async () => {
    try {
      setLoading(true);
      
      // 构建缓存键
      const cacheKey = 'topcompanies_marketcap';
      
      // 检查缓存是否有效
      if (isCacheValid(cacheKey)) {
        const cachedData = getFromCache(cacheKey);
        if (cachedData) {
          setCompaniesData(cachedData);
          setCurrentApiType('marketCap');
          message.success(`從快取獲取 ${cachedData.length} 家市值最大的公司`);
          logger.log(`從快取載入數據，周期: ${getCurrentCyclePeriod()}`);
          setLoading(false);
          return;
        }
      }

      // 缓存无效，从数据库获取
      activityTracker.trackBtnExecution('top_companies', 'fetch_data', '獲取市值最大200檔股票');
      logger.log(`快取無效或不存在，從資料庫獲取數據`);

      const response = await apiClient.get(API_CONFIG.endpoints.TOPCOMPANIES.TOP_MARKET_CAP);
      
      if (response.data && response.data.length > 0) {
        setCompaniesData(response.data);
        setCurrentApiType('marketCap');
        
        // 保存到缓存
        saveToCache(cacheKey, response.data);
        
        message.success(`從資料庫獲取 ${response.data.length} 家市值最大的公司`);
      } else {
        message.warning('未找到符合條件的公司數據');
      }
    } catch (error) {
      logger.error('獲取市值最大200檔股票時出錯:', error);
      message.error('獲取數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };


  // 處理 sector 選擇
  const handleSectorChange = (value) => {
    setSelectedSector(value);
    // 資料載入由依賴 selectedSector 的 useEffect 負責
  };

  const handleTableViewChange = (viewType) => {
    setTableViewType(viewType);
  };


  // 表格列定義 - 為公司代碼欄位添加點擊事件處理
  const getColumns = () => {
    const addClickHandlers = (columns) => {
      return columns.map(col => {
        if (col.dataIndex === 'company_symbol') {
          return {
            ...col,
            render: (text, record) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                  flexShrink: 0
                }}>
                  {record.image ? (
                    <img 
                      src={record.image} 
                      alt={text} 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain' 
                      }}
                      onError={(e) => {
                        try {
                          const imgEl = e.currentTarget;
                          if (imgEl && imgEl.style) {
                            imgEl.style.display = 'none';
                          }
                          const parent = imgEl ? imgEl.parentElement : null;
                          if (!parent) return;
                          // 移除可能已存在的fallback
                          const existing = parent.querySelector('.img-fallback-initial');
                          if (existing) existing.remove();
                          const fallback = document.createElement('span');
                          fallback.className = 'img-fallback-initial';
                          fallback.textContent = (text && typeof text === 'string') ? text[0] : 'N/A';
                          fallback.style.fontSize = '12px';
                          fallback.style.color = '#999';
                          parent.appendChild(fallback);
                        } catch (_) {
                          // 忽略任何錯誤，避免影響渲染
                        }
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      {text ? text[0] : 'N/A'}
                    </span>
                  )}
                </div>
                <a 
                  onClick={() => handleSymbolClick(text)}
                  onContextMenu={(e) => handleSymbolContextMenu(e, text)}
                  style={{ 
                    color: '#1890ff',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: '13px'
                  }}
                  className="company-symbol-link"
                >
                  {text}
                </a>
              </div>
            )
          };
        }
        
        
        return col;
      });
    };

    switch (tableViewType) {
      case 'growth':
        return addClickHandlers(growthValuationColumns);
      case 'financial':
        return addClickHandlers(financialRatiosColumns);
      case 'price':
        return addClickHandlers(pricePerformanceColumns);
      case 'trend':
        return addClickHandlers(trendColumns);
      case 'charts':
        return []; // 圖表視圖不需要表格列
      case 'basic':
      default:
        return addClickHandlers(basicInfoColumns);
    }
  };

  // 使用列可見性控制Hook
  const { visibleColumns, setVisibleColumns, filteredColumns: visibilityFilteredColumns } = useColumnVisibility(getColumns());

  // 組件卸載時清理右鍵選單
  useEffect(() => {
    return () => {
      // 清理所有可能存在的右鍵選單
      const existingMenus = document.querySelectorAll('.symbol-context-menu');
      existingMenus.forEach(menu => {
        try {
          menu.remove();
        } catch (error) {
          // 忽略錯誤
        }
      });
    };
  }, []);

  return (
    <Container fluid className="mt-4">
      {/* 頁面標題 */}
          <Card style={{ marginBottom: '5px' }}>
            <div style={{ textAlign: 'center', padding: '5px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>

                <div className="top-tabs-nav" style={{ border: '1px solid #57A300', borderRadius: 5, display: 'flex', alignItems: 'center', margin: 0, padding: '6px 16px', backgroundColor: '#ff7a00', color: '#fff' }}>
                  市值大
                </div>
               
              <Select
                value={selectedSector}
                onChange={handleSectorChange}
                placeholder="選擇產業"
                style={{ width: 300 }}
                size="middle"
              >
                {sectorOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              </div>
            </div>
          </Card>

          {/* 載入狀態顯示 */}
          {loading && (
            <Card style={{ marginBottom: '20px' }}>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
                  正在載入中...
                </div>
              </div>
            </Card>
          )}

          {/* 數據顯示區域 */}
          {companiesData.length > 0 && (
        <Card style={{ marginTop: '20px' }}>
          {/* 表格视图导航栏 */}
            <Title level={5} style={{ margin: 0, color: '#57A300' }}>
                <DollarOutlined style={{ marginRight: '10px' }} />
                {currentApiType === 'sector' && selectedSector ? `${selectedSector} 產業前30大公司` : '市值前200大公司'}
            </Title>
          <Navbar className="table-view-navbar">
            <Nav className="me-auto nav-center">
              <Nav.Link 
                onClick={() => handleTableViewChange('basic')}
                className={tableViewType === 'basic' ? 'active' : ''}
              >
                <AppstoreOutlined style={{ marginRight: '6px' }} />
                {pageNames.basic}
              </Nav.Link>
              <Nav.Link
                onClick={() => handleTableViewChange('growth')}
                className={tableViewType === 'growth' ? 'active' : ''}
              >
                <BarChartOutlined style={{ marginRight: '6px' }} />
                {pageNames.growth}
              </Nav.Link>
              <Nav.Link 
                onClick={() => handleTableViewChange('financial')}
                className={tableViewType === 'financial' ? 'active' : ''}
              >
                <BarChartOutlined style={{ marginRight: '6px' }} />
                {pageNames.financial}
              </Nav.Link>
              <Nav.Link 
                onClick={() => handleTableViewChange('price')}
                className={tableViewType === 'price' ? 'active' : ''}
              >
                <RiseOutlined style={{ marginRight: '6px' }} />
                {pageNames.price}
              </Nav.Link>
              <Nav.Link 
                onClick={() => handleTableViewChange('trend')}
                className={tableViewType === 'trend' ? 'active' : ''}
              >
                <LineChartOutlined style={{ marginRight: '6px' }} />
                {pageNames.trend}
              </Nav.Link>
              <Nav.Link 
                onClick={() => handleTableViewChange('charts')}
                className={tableViewType === 'charts' ? 'active' : ''}
              >
                <StockOutlined style={{ marginRight: '6px' }} />
                {pageNames.technical}
              </Nav.Link>
            </Nav>
          </Navbar>

          {/* 技術分析圖表視圖 */}
          {tableViewType === 'charts' && (
            <TechnicalChartsPage screeningResults={companiesData} />
          )}
          
          {/* 表格視圖 */}
          {tableViewType !== 'charts' && (
            <div ref={tableContainerRef}>
              <TableControlBar
                columns={getColumns()}
                visibleColumns={visibleColumns}
                onChange={setVisibleColumns}
                tableContainerRef={tableContainerRef}
              />
              <Table
                columns={visibilityFilteredColumns}
                dataSource={companiesData}
                rowKey={(record) => record.company_symbol || record.symbol || `${record.sector || 'row'}-${record.company_name || Math.random()}`}
                loading={loading}
                size="small"
                bordered
                pagination={{
                  defaultPageSize: 100,
                  showSizeChanger: true,
                  pageSizeOptions: ['20', '50', '100', '200'],
                }}
                scroll={{ x: 'max-content' }}
                style={{ overflowX: 'auto' }}
                tableLayout="fixed"
                className="stock-screener-table"
                rowClassName={(record, index) => index % 2 === 0 ? 'even-row' : 'odd-row'}
                components={{
                  header: {
                    cell: props => {
                      if (props.className && props.className.includes('icon-column')) {
                        return <th {...props} style={{ ...props.style, width: 30, maxWidth: 30, minWidth: 30 }} />;
                      }
                      return <th {...props} />;
                    }
                  },
                  body: {
                    cell: props => {
                      if (props.className && props.className.includes('icon-column')) {
                        return <td {...props} style={{ ...props.style, width: 30, maxWidth: 30, minWidth: 30 }} />;
                      }
                      return <td {...props} />;
                    }
                  }
                }}
              />
            </div>
          )}
        </Card>
      )}

      <style jsx>{`
        /* Top tabs styles */
        .top-tabs-nav {
          border: 1px solid #57A300;
          border-radius: 5px;
        }
        .top-tabs-link {
          padding: 6px 16px;
          border-right: 1px solid #57A300; /* 分隔線 */
        }
        .top-tabs-link:last-child {
          border-right: none;
        }
        .top-tabs-link.active {
          background: #ff7a00; /* 橘色 */
          color: #fff !important;
        }

        /* 表格视图导航栏样式 */
        .table-view-navbar {
          background: none;
          border: none;
          box-shadow: none;
          margin: 0 0 15px 0;
          padding: 0;
        }
        .table-view-navbar .nav {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: center;
          gap: 0;
          overflow: hidden;
          border-radius: 24px;
          border: 2px solid #57A300;
          background: #fff;
          padding: 0;
        }
        .table-view-navbar .nav-center {
          justify-content: center;
          width: 100%;
        }
        .table-view-navbar .nav-link {
          border: 1px solid #57A300;
          background: #fff;
          color: #57A300;
          font-size: 14px;
          padding: 6px 20px;
          border-radius: 0;
          transition: background 0.2s, color 0.2s;
          box-shadow: none;
          position: relative;
          font-weight: 500;
          margin-left: -1px;
          cursor: pointer;
        }
        .table-view-navbar .nav-link:first-child {
          border-top-left-radius: 24px;
          border-bottom-left-radius: 24px;
          margin-left: 0;
        }
        .table-view-navbar .nav-link:last-child {
          border-top-right-radius: 24px;
          border-bottom-right-radius: 24px;
        }
        .table-view-navbar .nav-link.active {
          background: #57A300;
          color: #fff;
          z-index: 1;
        }
        
        @media screen and (max-width: 480px) {
          .table-view-navbar .nav {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            white-space: nowrap !important;
            gap: 5px;
            justify-content: flex-start !important;
          }
          .table-view-navbar .nav-link {
            font-size: 12px !important;
            padding: 4px 12px !important;
          }
        }
      `}</style>

      {/* 添加自定义图标列样式 */}
      <style global jsx>{`
        .icon-column {
          width: 30px !important;
          min-width: 30px !important;
          max-width: 30px !important;
          padding: 0 !important;
          overflow: hidden !important;
        }
        .ant-table-cell.icon-column {
          width: 30px !important;
          min-width: 30px !important;
          max-width: 30px !important;
        }
        .ant-table-thead > tr > th.icon-column {
          width: 30px !important;
          min-width: 30px !important;
          max-width: 30px !important;
        }
        .ant-table-thead > tr > th.icon-column::before {
          display: none !important;
        }
        
        /* 公司代码链接样式 */
        .company-symbol-link:hover {
          color: #40a9ff !important;
          text-decoration: underline !important;
        }
        
        /* 强制表格遵守列宽设置 */
        .stock-screener-table .ant-table table {
          table-layout: fixed !important;
          width: 100% !important;
        }
        
        .stock-screener-table .ant-table-cell {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        /* 表头允许换行 */
        .stock-screener-table .ant-table-thead > tr > th {
          white-space: normal !important;
          word-wrap: break-word !important;
          word-break: break-word !important;
          line-height: 1.2 !important;
          vertical-align: middle !important;
        }
        
        .stock-screener-table .ant-table-thead > tr > th,
        .stock-screener-table .ant-table-tbody > tr > td {
          box-sizing: border-box !important;
          padding: 4px 8px !important;
        }
        
        /* 设置表头样式为绿色背景白色文字 */
        .stock-screener-table .ant-table-thead > tr > th {
          background-color: #57A300 !important;
          color: white !important;
          font-weight: 500 !important;
        }
        
        /* 去除表头分割线 */
        .stock-screener-table .ant-table-thead > tr > th::before {
          display: none !important;
        }
        
        /* 设置行样式 */
        .stock-screener-table .ant-table-tbody > tr.even-row > td {
          background-color: #f8f8f8;
        }
        
        .stock-screener-table .ant-table-tbody > tr.odd-row > td {
          background-color: #ffffff;
        }
        
        /* 鼠标悬停效果 */
        .stock-screener-table .ant-table-tbody > tr:hover > td {
          background-color: #e6f7e1 !important; /* 淡绿色 */
        }
        
        /* 确保图片容器不会扩张 */
        .stock-screener-table img {
          max-width: 100% !important;
          max-height: 100% !important;
        }
      `}</style>

      {/* 登录对话框 */}
      <LoginDialog 
        open={showLoginDialog} 
        onClose={() => setShowLoginDialog(false)} 
        openRegister={() => {
          setShowLoginDialog(false); 
          setShowRegisterDialog(true);
        }}
        isLimitReached={false}
      />

      {/* 注册对话框 */}
      <RegisterDialog 
        open={showRegisterDialog} 
        onClose={() => setShowRegisterDialog(false)}
        openLogin={() => {
          setShowRegisterDialog(false); 
          setShowLoginDialog(true);
        }}
      />

    </Container>
  );
}

export default TopCompanies;
